import { useHistory } from 'react-router';
import { bindActionCreators, Dispatch } from 'redux';
import { connect, RootStateOrAny } from 'react-redux';
import { useEffect } from 'react';

import { Input, Card, Button, Form, Table } from 'antd';

import notifier from 'utils/notifier';
import { ErrorResponse } from 'utils/types';

import { checkPasswordHistory, resetPassword } from 'store/auth/actions';

import FloatLabel from 'components/Common/FloatLabel';

import { ResetPasswordProps } from './Proptypes';
import { setApiServer } from 'constants/api';

import styles from "./ResetPassword.module.css"

const ResetPasswordComponent = (props: ResetPasswordProps) => {

  const history = useHistory();
  const [form] = Form.useForm();
  const RESET_PASSWORD_SUCCESS = 0;
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const urlResetToken = urlParams.get("reset_code")?.toString();
  const userId = urlParams.get("username")?.toString();
  let API_ROUTE = urlParams.get("ipa")?.toString().toUpperCase();

  useEffect(() => {
    //change API routing key for NMM and BA after changing BA configs to BAIPA for QA environment
    if (API_ROUTE === 'NMM') {
      API_ROUTE = 'APC';
    }
    else if (API_ROUTE === 'BA') {
      API_ROUTE = 'BAIPA'
    }
    setApiServer(API_ROUTE!);
  }, [API_ROUTE])

  const checkPasswordHistory = (newPassword: string) => {
    return props.checkPasswordHistory(userId!, newPassword)
      .then((response: boolean) => {
        return response;
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
        return false;
      });
  };
  
  const submit = () => {
    form.validateFields()
      .then(async values => {
        if(userId && urlResetToken){
          const {newPassword, newPasswordConfirm} = values;
          props.resetPassword(userId!, urlResetToken!, newPassword)
          .then((response: number) => {
            if (response === RESET_PASSWORD_SUCCESS) {
              form.resetFields();
              history.push("/");
              notifier.success('Password Reset Success!');
            } else {
              notifier.error('Something went wrong.');
            }
              return response;
          }) 
          .catch((error: ErrorResponse) => {
            notifier.error(error.response?.data.Message);
          });  
        } else{
          notifier.error('Error, perhaps try with a new link from the login page');
        }
      })
      .catch(err => {
        notifier.error('Check your password entries.');
      });
  };

  return (
    <>
    <div className={styles["profile-page"]}>
      <Card className={styles["profile-form"]}>
        <Form
          form={form}
          name="resetPasswordForm"
          onFinish={submit}
        >

          <FloatLabel 
            label="New Password" 
            name="newPassword"
            form={form}
            validateTrigger={['onChange', 'onBlur']}
            dependencies={['newPasswordConfirm']}
            required
            rules={[
              () => ({
                async validator(_: any, value: string) {
                  let checkHistory = value && await checkPasswordHistory(value);
                  if (!value || 
                      (value.match(/^((?=.*?[A-Z])(?=.*?[a-z])(?=.*[^A-Za-z0-9\s])|(?=.*?[0-9])(?=.*?[a-z])(?=.*[^A-Za-z0-9\s])|(?=.*?[0-9])(?=.*?[A-Z])(?=.*[^A-Za-z0-9\s])|(?=.*?[0-9])(?=.*?[A-Z])(?=.*?[a-z]))(?=.{6,})/) && 
                      !value.includes(userId!) && !checkHistory
                    )) {
                      return Promise.resolve();
                    } else if (checkHistory) {
                      return Promise.reject(new Error('Please enter a different password from your previous ones.'));
                    }
                    return Promise.reject(new Error('Does not meet the password requirements below.'));
                },
              }),
            ]}
          >
            <Input
              type="password"
              autoComplete="new-password"
            />
          </FloatLabel>

          <FloatLabel 
            label="Confirm New Password" 
            name="newPasswordConfirm"
            form={form}
            validateTrigger={['onBlur']}
            dependencies={['newPassword']}
            required
            rules={[
              ({ getFieldValue } : any) => ({
                async validator(_: any, value: string) {
                  if (!value || value === getFieldValue('newPassword')) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match.'));
                },
              }),
            ]}
          >
            <Input
              type="password"
            />
          </FloatLabel>
          <div className={styles["btn-wrapper"] + " d-flex justify-content-end"}>
            <Button type="primary" className='mr-2' loading={props.isResettingPass} onClick={submit}>Submit</Button>
          </div>
        </Form>
      </Card>
    </div>
    <div className="mt-3">
      <Table 
        columns={[
          {
            title: 'New Password must meet the following requirements:',
            key: 'requirements',
            dataIndex: 'requirements'
          },
          {
            title: 'Example:',
            key: 'example',
            dataIndex: 'example'
          }
        ]}
        dataSource={[
          {
            key: 1,
            requirements: <>
            1. Cannot be the same as your current password.
            <br />
            2. Cannot contain your user ID.
            <br />
            3. Must be at least 6 characters long.
            <br />
            4. Must contain characters that meet at least 3 criteria:
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;a. Uppercase characters (A through Z);
                <br />
            &nbsp;&nbsp;&nbsp;&nbsp;b. Lowercase characters (a through z);
                <br />
            &nbsp;&nbsp;&nbsp;&nbsp;c. Number (0 through 9)
                <br />
            &nbsp;&nbsp;&nbsp;&nbsp;d. Special characters ( ~!@#$%^&*(|) ); </>,
            example: <> User ID: Adam123
            <br />
            <br />
            New Password: Adam12301 (Invalid - contains user ID)
            <br />
            New Password: Zadam12301 (Invalid - contains user ID)
            <br />
            New Password: Adam783 (Valid)
            <br />
            New Password: A!dam123 (Valid)
            <br />
            New Password: a!dam123 (Valid) </>,
          },
        ]}
        pagination={false}
      />
    </div>
    </>
  );
};

const mapStateToProps = (store: RootStateOrAny) => ({
  isCheckingHistory: store.rootReducer.auth.isCheckingHistory,
  isResettingPass: store.rootReducer.auth.isResettingPass,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
  {
    checkPasswordHistory,
    resetPassword,
  },
  dispatch,
);

export default connect(mapStateToProps, mapDispatchToProps)(ResetPasswordComponent);